import React, { useEffect, useState } from 'react';
import { t } from 'i18next';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import ModelTooltips from '../../../../shared/components/model-tooltips/ModelTooltips';
import {
  PageMovementMode,
  PageMovementRow,
} from '../../../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import { ChatType } from '../../../../shared/types/monitor/ChatType';
import { useGetPageMovementForDomain } from '../../../../shared/api/hooks/monitor/data/sources/UseGetPageMovementForDomain';
import PageMovementTable from './PageMovementTable';
import './PageMovementSection.scss';

const PAGE_SIZE = 5;

interface TabDef {
  mode: PageMovementMode;
  i18nLabel: string;
  i18nTooltip: string;
  icon: string;
}

const TABS: TabDef[] = [
  {
    mode: 'top',
    i18nLabel: 'pageMovementTabTop',
    i18nTooltip: 'pageMovementTabTopTooltip',
    icon: 'ico--star',
  },
  {
    mode: 'topGainers',
    i18nLabel: 'pageMovementTabTopGainers',
    i18nTooltip: 'pageMovementTabTopGainersTooltip',
    icon: 'ico--chart-up',
  },
  {
    mode: 'topDecliners',
    i18nLabel: 'pageMovementTabTopDecliners',
    i18nTooltip: 'pageMovementTabTopDeclinersTooltip',
    icon: 'ico--chart-down',
  },
  {
    mode: 'newlyCited',
    i18nLabel: 'pageMovementTabNewlyCited',
    i18nTooltip: 'pageMovementTabNewlyCitedTooltip',
    icon: 'ico--zap',
  },
  {
    mode: 'droppedOff',
    i18nLabel: 'pageMovementTabDroppedOff',
    i18nTooltip: 'pageMovementTabDroppedOffTooltip',
    icon: 'ico--logout',
  },
];

interface PageMovementSectionProps {
  projectId: number;
  domain: string;
  chatType: ChatType | null;
  from: Date | null;
  to: Date | null;
  monitorPromptIds?: number[];
  filterMonitorTypes?: string[];
}

const PageMovementSection: React.FC<PageMovementSectionProps> = ({
  projectId,
  domain,
  chatType,
  from,
  to,
  monitorPromptIds,
  filterMonitorTypes,
}) => {
  const [mode, setMode] = useState<PageMovementMode>('top');
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState<PageMovementRow[]>([]);
  const [lastPageLoaded, setLastPageLoaded] = useState(0);

  // Reset accumulator when tab/filter changes. Without this, switching tabs would prepend
  // the previous tab's rows onto the new mode's list.
  useEffect(() => {
    setPage(1);
    setAccumulated([]);
    setLastPageLoaded(0);
  }, [mode, projectId, domain, chatType, from, to, monitorPromptIds, filterMonitorTypes]);

  const { pageMovement, isLoading } = useGetPageMovementForDomain(
    projectId,
    domain,
    mode,
    page,
    PAGE_SIZE,
    chatType,
    from,
    to,
    monitorPromptIds,
    filterMonitorTypes
  );

  useEffect(() => {
    if (!pageMovement) return;
    if (page === lastPageLoaded) return;
    setAccumulated((prev) => {
      if (page === 1) return pageMovement.rows;
      return [...prev, ...pageMovement.rows];
    });
    setLastPageLoaded(page);
  }, [pageMovement, page, lastPageLoaded]);

  const totalItems = pageMovement?.pagination.totalItems ?? 0;
  const remaining = Math.max(0, totalItems - accumulated.length);

  return (
    <div className="stats__panel page-movement-section">
      {/* Mounts global tooltips on every `.ico--xxx-bck` icon class on the page so the
          visible model icons show their names on hover. The same map drives the overflow
          tooltip in ModelIconsList — see shared/components/model-tooltips. */}
      <ModelTooltips />
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('pageMovement')}
          <CustomTooltip tooltipText="pageMovementTooltip" addMargin={false} />
        </div>
      </div>

      {/* Reuses the global `.tabs` / `.tabs__item` styling from shared Tabs.scss so the
          look matches the Settings tabs and the rest of the app. The shared Tabs component
          itself is URL-driven (<Link>) — here we keep the mode as local state, so we render
          buttons with the same class names instead of importing the component directly. */}
      <div className="stats-box">
        <div className="tabs page-movement-section__tabs" role="tablist">
          {TABS.map((tab) => {
            const isActive = tab.mode === mode;
            const tooltipId = `page-movement-tab-${tab.mode}`;
            return (
              <React.Fragment key={tab.mode}>
                <button
                  type="button"
                  role="tab"
                  id={tooltipId}
                  className={`tabs__item ${isActive ? 'tabs__item--active' : ''}`}
                  aria-selected={isActive}
                  onClick={() => setMode(tab.mode)}
                >
                  <span className={`${tab.icon} ico--xs page-movement-section__tab-icon`} />
                  {t(tab.i18nLabel)}
                </button>
                <CustomTooltip tooltipText={tab.i18nTooltip} targetRef={tooltipId} />
              </React.Fragment>
            );
          })}
        </div>

        {isLoading && accumulated.length === 0 ? (
          // Shared table skeleton — same `skeleton--table` markup as DomainsStatsTable and
          // WatchedPagesSection so the loading state is consistent across the SourcesPage.
          // Kept inside `__body` so the min-height reservation prevents a scroll snap on tab
          // switch (the standalone skeleton is shorter than the loaded 5-row table).
          <div className="page-movement-section__body">
            <Skeleton className="skeleton--table skeleton--table-5">
              <span />
              <span />
              <span />
              <span />
              <span />
            </Skeleton>
          </div>
        ) : accumulated.length === 0 ? (
          <div className="page-movement-section__body page-movement-section__body--empty">
            <EmptyPanel />
          </div>
        ) : (
          // `key={mode}` re-mounts the body on tab switch so the fade-in keyframe replays
          // (without it, React would diff in place and skip the animation).
          <div className="page-movement-section__body" key={mode}>
            <PageMovementTable rows={accumulated} mode={mode} projectId={projectId} />
            {remaining > 0 && (
              <div className="page-movement-section__footer">
                <Button
                  className="btn btn--sm btn--secondary"
                  label={t('loadMoreN', { count: Math.min(PAGE_SIZE, remaining) })}
                  onClick={() => setPage((p) => p + 1)}
                  disabled={isLoading}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageMovementSection;
